import { computed, ref, watch } from 'vue'

export const OWNER_STEP = 1
export const TYPE_STEP = 2
export const LOCATION_STEP = 3
export const UNITS_STEP = 4
export const DONE_STEP = 5

/** Nach diesem Schritt entsteht das Objekt – danach folgt nur noch die Bestätigung. */
export const LAST_FORM_STEP = UNITS_STEP

const stepOfField: Record<string, number> = {
  owner_type: OWNER_STEP,
  owner_id: OWNER_STEP,
  estate_object_type: TYPE_STEP,
  google_place_id: LOCATION_STEP,
  street: LOCATION_STEP,
  house_number: LOCATION_STEP,
  address_addition: LOCATION_STEP,
  zip_code: LOCATION_STEP,
  city: LOCATION_STEP,
  state: LOCATION_STEP,
  living_space_size: UNITS_STEP,
  business_space_size: UNITS_STEP,
  number_of_units: UNITS_STEP,
}

const stepOfError = (field: string): number => stepOfField[field] ?? LAST_FORM_STEP

const scrollToTop = (): void => window.scrollTo({ top: 0, behavior: 'smooth' })

export function usePropertyCreateSteps(
  createdId: () => string | undefined,
  errors: () => Partial<Record<string, string>>,
) {
  const step = ref<number>(createdId() ? DONE_STEP : OWNER_STEP)

  const locked = computed(() => Boolean(createdId()))

  const stepErrors = computed(() =>
    Object.entries(errors())
      .filter(([field]) => stepOfError(field) === step.value)
      .map(([, message]) => message)
      .filter((message): message is string => !!message),
  )

  watch(createdId, (id) => {
    if (id) {
      step.value = DONE_STEP
    }
  })

  function canReturnTo(target: number): boolean {
    return !locked.value && target >= OWNER_STEP && target < step.value
  }

  function goBackTo(target: number): void {
    if (!canReturnTo(target)) {
      return
    }

    step.value = target
    scrollToTop()
  }

  function advance(): void {
    step.value = Math.min(step.value + 1, DONE_STEP)
    scrollToTop()
  }

  function showFirstRejectedStep(rejected: Record<string, string>): void {
    const steps = Object.keys(rejected).map(stepOfError)

    step.value = steps.length ? Math.min(...steps) : step.value
  }

  return { step, locked, stepErrors, canReturnTo, goBackTo, advance, showFirstRejectedStep }
}
